import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Container,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BarChartIcon from '@mui/icons-material/BarChart';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { useAuth } from '../hooks/useAuth';
import { getDashboardStats, getReports } from '../services/reportService';
import { BRAND } from '../theme/brandTheme';
import {
  CroKpiCards,
  BrandOverviewCards,
  DashboardSkeleton,
} from '../components/dashboard';
import PortfolioReport from '../components/dashboard/PortfolioReport';
import { getOutcomeChip } from '../utils/reportTaxonomy';

/** Read-only stakeholder view — brands, KPIs, export */
const StakeholderPortal = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portfolioOpen, setPortfolioOpen] = useState(false);

  const brandIds = useMemo(() => user?.brands?.map((b) => b._id) || [], [user]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsData, reportsData] = await Promise.all([
        getDashboardStats(brandIds),
        getReports(brandIds),
      ]);
      setStats(statsData);
      setReports(reportsData || []);
    } catch (err) {
      console.error('Stakeholder portal load error:', err);
    } finally {
      setLoading(false);
    }
  }, [brandIds]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  const recentWinners = useMemo(() => {
    const flat = (reports || []).flatMap((brand) =>
      (brand.tests || []).map((test) => ({
        ...test,
        brandName: brand.name,
        brandId: brand._id,
      })),
    );
    return flat
      .filter((t) => t.resultStatuses?.includes('Kazandı'))
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .slice(0, 5);
  }, [reports]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (!brandIds.length) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h5" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          Henüz size atanmış bir marka bulunmamaktadır.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3 }}>
        <Chip
          icon={<VisibilityIcon />}
          label="Stakeholder — salt okunur görünüm"
          size="small"
          sx={{ mb: 1.5, bgcolor: `${BRAND.purple}14`, color: BRAND.purple, fontWeight: 600 }}
        />
        <Typography variant="h4" sx={{ fontWeight: 700, color: BRAND.purple, mb: 1 }}>
          Portföy Özeti
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Atandığınız markaların test sonuçlarını görüntüleyin ve raporları dışa aktarın.
        </Typography>
      </Box>

      <CroKpiCards croKpis={stats?.croKpis} user={user} hideLeaderboard />

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<SummarizeIcon />}
          onClick={() => setPortfolioOpen(true)}
          sx={{ borderColor: BRAND.purple, color: BRAND.purple }}
        >
          Portföy Raporu (PDF/CSV)
        </Button>
        <Button
          component={RouterLink}
          to="/dashboard/statistics"
          variant="outlined"
          size="small"
          startIcon={<BarChartIcon />}
          sx={{ borderColor: BRAND.purple, color: BRAND.purple, textTransform: 'none' }}
        >
          İstatistikler
        </Button>
      </Box>

      <BrandOverviewCards brands={reports} />

      {recentWinners.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.purple, mb: 2 }}>
            Son Kazanan Testler
          </Typography>
          <Box sx={{ display: 'grid', gap: 1 }}>
            {recentWinners.map((test) => {
              const outcome = getOutcomeChip(test.resultStatuses || []);
              const reportId = test.id || test._id;
              return (
                <Button
                  key={reportId}
                  component={RouterLink}
                  to={`/dashboard/reports/${reportId}`}
                  variant="outlined"
                  sx={{
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    py: 1.25,
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {test.name || test.reportName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {test.brandName}
                    </Typography>
                  </Box>
                  <Chip label={outcome.label} size="small" color={outcome.color} />
                </Button>
              );
            })}
          </Box>
        </Box>
      )}

      <PortfolioReport
        open={portfolioOpen}
        onClose={() => setPortfolioOpen(false)}
        brands={reports}
        croKpis={stats?.croKpis}
      />
    </Container>
  );
};

export default StakeholderPortal;
